<template>
  <section class="services-section">
    <v-container fluid class="py-12 services-container">
      <v-row justify="center" class="mb-8">
        <v-col data-aos="flip-right" cols="12" md="8" class="text-center">
          <h2 class="section-title">
            <span class="title-gradient">Our Services</span>
          </h2>
          <p class="section-subtitle">
            Expert solutions across home & property — dependable, quick and affordable.
          </p>
        </v-col>
      </v-row>

      <v-carousel
        data-aos="flip-up"
        v-model="carousel"
        cycle
        interval="3500"
        hide-delimiter-background
        delimiter-icon="mdi-circle"
        :show-arrows="false"
        height="auto"
        class="services-carousel"
      >
        <v-carousel-item
          v-for="(chunk, index) in chunkedServices"
          :key="index"
          class="carousel-item"
        >
          <v-row class="ma-0 services-row">
            <v-col
              v-for="(service, idx) in chunk"
              :key="service.title"
              cols="12"
              md="4"
              class="d-flex pa-3 service-col"
            >
              <article
                class="service-card"
                :data-idx="idx"
                ref="cards"
                role="article"
                :aria-labelledby="service.title"
              >
                <div class="card-top">
                  <div class="icon-circle" aria-hidden="true">
                    <v-icon size="28" color="#ff6600">{{ service.icon }}</v-icon>
                  </div>
                  <div class="card-title-group">
                    <h3 class="service-title">{{ service.title }}</h3>
                    <p class="service-short">{{ service.short }}</p>
                  </div>
                </div>

                <div class="card-body">
                  <p class="service-desc">{{ service.desc }}</p>
                </div>
              </article>
            </v-col>
          </v-row>
        </v-carousel-item>
      </v-carousel>
    </v-container>
  </section>
</template>

<script>
export default {
  name: 'OurServicesSection',
  data() {
    return {
      carousel: 0,
      services: [
        {
          title: 'Home Appliance Repair',
          short: 'Fast doorstep repairs',
          desc: 'Fridges, washing machines, microwaves and more.',
          icon: 'mdi-laptop',
        },
        {
          title: 'Cleaning Works',
          short: 'Basic & deep cleaning',
          desc: 'Upholstery, kitchens, bathrooms — eco-friendly supplies.',
          icon: 'mdi-broom',
        },
        {
          title: 'Painting',
          short: 'Interior & exterior painting',
          desc: 'Surface prep, premium paints, color consultation.',
          icon: 'mdi-format-paint',
        },
        {
          title: 'Gardening',
          short: 'Lawn care & landscaping',
          desc: 'Pruning, lawn upgrades, landscape installations.',
          icon: 'mdi-flower',
        },
        {
          title: 'Construction',
          short: 'Renovations & improvements',
          desc: 'Licensed contractors for safe, timely projects.',
          icon: 'mdi-hammer-wrench',
        },
        {
          title: 'Interior Works',
          short: 'Carpentry & fixtures',
          desc: 'Custom carpentry, installations, interior upgrades.',
          icon: 'mdi-sofa',
        },
        {
          title: 'Electrical Works',
          short: 'Wiring & lighting',
          desc: 'Certified electricians, inspections, installations.',
          icon: 'mdi-power-plug',
        },
        {
          title: 'Plumbing',
          short: 'Leak fixes & maintenance',
          desc: 'Rapid leak repairs, bathroom fittings, installations.',
          icon: 'mdi-water-pump',
        },
        {
          title: 'Air Conditioner Repair',
          short: 'AC servicing & repair',
          desc: 'Gas top-ups, servicing, emergency HVAC repairs.',
          icon: 'mdi-air-conditioner',
        },
      ],
    };
  },
  computed: {
    chunkedServices() {
      const chunks = [];
      const size = 3;
      for (let i = 0; i < this.services.length; i += size) {
        chunks.push(this.services.slice(i, i + size));
      }
      return chunks;
    },
  },
  mounted() {
    this.$nextTick(() => {
      const cards = this.$refs.cards || [];
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('animate-in');
              io.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.12 }
      );

      if (Array.isArray(cards)) {
        cards.forEach((c) => io.observe(c));
      } else if (cards) {
        io.observe(cards);
      }
    });
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Nunito+Sans:ital,opsz,wght@0,6..12,200..1000;1,6..12,200..1000&display=swap');

* {
  box-sizing: border-box;
}

.services-section {
  font-family: 'Nunito Sans', sans-serif;
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
}

.services-container {
  width: 100%;
  max-width: 100%;
  padding-left: 16px;
  padding-right: 16px;
}

/* Section Header */
.section-title {
  font-size: 3.5rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 0.5rem;
  letter-spacing: -0.05rem;
}

.title-gradient {
  background: linear-gradient(90deg, #f46523 0%, #f46523 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.section-subtitle {
  font-size: 1.25rem;
  color: #6c757d;
  font-weight: 400;
}

.services-carousel {
  width: 100%;
  max-width: 100%;
  padding-bottom: 40px;
}

.services-row {
  width: 100%;
  max-width: 100%;
}

.service-col {
  width: 100%;
  max-width: 100%;
}

.service-card {
  background: #ffffff;
  border-radius: 12px;
  padding: 30px;
  border-left: 5px solid transparent;
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;

  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  transition:
    transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1),
    box-shadow 0.3s cubic-bezier(0.25, 0.8, 0.25, 1),
    border-left-color 0.3s ease;
}

.service-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 15px 45px rgba(0, 0, 0, 0.18);
  border-left-color: #f46523;
}

.card-top {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.icon-circle {
  min-width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: #e3f2fd;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 20px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
}

.icon-circle .v-icon {
  color: #f46523 !important;
}

.card-title-group {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.service-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #212529;
  margin: 0;
  word-wrap: break-word;
}

.service-short {
  font-size: 0.95rem;
  color: #6c757d;
  font-weight: 500;
  margin: 0;
}

.card-body {
  flex-grow: 1;
}

.service-desc {
  font-size: 1rem;
  line-height: 1.6;
  color: #495057;
  margin: 0;
  word-wrap: break-word;
}

.service-card.animate-in {
  opacity: 1;
  transform: translateY(0);
}

.service-card[data-idx='0'] {
  transition-delay: 0.1s;
}

.service-card[data-idx='1'] {
  transition-delay: 0.2s;
}

.service-card[data-idx='2'] {
  transition-delay: 0.3s;
}

.v-carousel__controls {
  bottom: 10px !important;
}

.v-carousel__controls .v-btn--icon.v-btn--active {
  color: #f46523 !important;
}

.v-carousel__controls .v-btn--icon {
  color: #ccc !important;
  opacity: 1;
}

@media (max-width: 960px) {
  .service-card {
    margin-bottom: 20px;
  }

  .services-container {
    padding-left: 12px;
    padding-right: 12px;
  }
}

@media (max-width: 768px) {
  .services-section {
    width: 100vw;
    max-width: 100vw;
  }

  .services-container {
    padding-left: 8px;
    padding-right: 8px;
    width: 100%;
    max-width: 100%;
  }

  .service-card {
    padding: 20px;
    margin-bottom: 15px;
  }

  .card-top {
    margin-bottom: 15px;
  }

  .icon-circle {
    min-width: 50px;
    height: 50px;
    margin-right: 15px;
  }

  .service-title {
    font-size: 1.3rem;
  }

  .service-short {
    font-size: 0.9rem;
  }

  .service-desc {
    font-size: 0.95rem;
  }
}

@media (max-width: 600px) {
  .section-title {
    font-size: 2.5rem;
  }

  .section-subtitle {
    font-size: 1rem;
  }

  .services-section {
    width: 100vw;
    max-width: 100vw;
  }

  .services-container {
    padding-left: 5px;
    padding-right: 5px;
    width: 100%;
    max-width: 100%;
  }

  .service-card {
    padding: 16px;
    margin-bottom: 12px;
  }

  .card-top {
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 12px;
  }

  .icon-circle {
    margin-right: 0;
    margin-bottom: 10px;
    align-self: center;
  }

  .card-title-group {
    text-align: center;
    width: 100%;
  }

  .service-title {
    font-size: 1.2rem;
  }

  .service-short {
    font-size: 0.85rem;
  }

  .service-desc {
    font-size: 0.9rem;
    text-align: center;
  }
}

@media (max-width: 480px) {
  .services-container {
    padding-left: 3px;
    padding-right: 3px;
  }

  .service-card {
    padding: 14px;
    border-radius: 8px;
  }

  .section-title {
    font-size: 2rem;
  }

  .icon-circle {
    min-width: 45px;
    height: 45px;
  }

  .service-title {
    font-size: 1.1rem;
  }

  .service-short {
    font-size: 0.8rem;
  }

  .service-desc {
    font-size: 0.85rem;
  }
}

@media (max-width: 375px) {
  .services-container {
    padding-left: 2px;
    padding-right: 2px;
  }

  .service-card {
    padding: 12px;
  }

  .section-title {
    font-size: 1.8rem;
  }

  .icon-circle {
    min-width: 40px;
    height: 40px;
  }

  .service-title {
    font-size: 1rem;
  }

  .service-short {
    font-size: 0.75rem;
  }

  .service-desc {
    font-size: 0.8rem;
  }
}

@media (max-width: 600px) {
  .v-container {
    padding-left: 0 !important;
    padding-right: 0 !important;
    margin: 0 !important;
    max-width: 100% !important;
    width: 100% !important;
  }

  .v-row {
    margin-left: 0 !important;
    margin-right: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
  }

  .v-col {
    padding-left: 4px !important;
    padding-right: 4px !important;
    width: 100% !important;
    max-width: 100% !important;
  }

  .v-carousel-item {
    width: 100% !important;
    max-width: 100% !important;
  }
}

body {
  overflow-x: hidden !important;
  width: 100%;
  max-width: 100%;
}

html {
  overflow-x: hidden !important;
  width: 100%;
  max-width: 100%;
}
</style>
